import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE_URL } from "../apiConfig";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import AppDialog from "./AppDialog";

// --- 1. Booking Details (Receipt) Modal ---
const BookingDetailsModal = ({ open, onClose, data, downloadReceipts }) => {
  const DetailRow = ({ label, value }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right" }}>
        {value ?? "-"}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#0B0F19",
          color: "white",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.1)",
          p: 2,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "white",
          bgcolor: "rgba(255,255,255,0.1)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
        }}
      >
        <CloseIcon />
      </IconButton>
      {data.playerDetails?.map((player, idx) => (
        <Box
          key={idx}
          sx={{
            mb: 3,
            p: 2,
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
            Player {idx + 1}
          </Typography>

          <DetailRow
            label="Player Name"
            value={`${player.firstName} ${player.lastName}`}
          />
          <DetailRow
            label="Phone Number"
            value={player.contactNumber || "N/A"}
          />
          <DetailRow label="Station" value={data.station?.name || "N/A"} />
          <DetailRow label="Station Type" value={data.station?.type || "-"} />
          {player.vrPlay && (
            <DetailRow
              label="VR Play"
              value={player.vrPlay === "yes" ? "Yes" : "No"}
            />
          )}

          <DetailRow label="Date" value={data.date || "N/A"} />
          <DetailRow label="Start Time" value={data.startTime || "N/A"} />
          <DetailRow label="Duration" value={data.duration || "N/A"} />
          {/* Divider & Payment Total */}
          <Box
            sx={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              pt: 2,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              Payment :
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ color: "#A905BC" }}
            >
              LKR {player.amount?.toFixed(2) || "0.00"}
            </Typography>
          </Box>
        </Box>
      ))}

      <Button
        type="button"
        fullWidth
        variant="contained"
        sx={{
          bgcolor: "#22C55E",
          color: "white",
          fontWeight: "bold",
          textTransform: "none",
          py: 1.5,
          borderRadius: "30px",
          "&:hover": { bgcolor: "#16A34A" },
        }}
        onClick={downloadReceipts}
      >
        Download Receipts
      </Button>
    </Dialog>
  );
};

// --- 3. Main Component ---
const PlayerInfo = ({
  selectedStation,
  selectedDateTime,
  onPlayerInfoChange,
  amount,
  onBookingSubmit,
  stationType,
}) => {
  const [dialog, setDialog] = useState({ open: false, type: "success", message: "" });
  const showDialog = (type, message) => setDialog({ open: true, type, message });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const [formData, setFormData] = useState({
    players: 1,
    playerDetails: [
      {
        firstName: "",
        lastName: "",
        contactNumber: "",
        email: "",
        vrPlay: "yes",
      },
    ],
  });

  const showVRPlay =
    selectedStation?.pricing?.some((p) => p.vrPrice && p.vrPrice > 0) || false;

  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setFormData((prev) => ({
        ...prev,
        playerDetails: [
          {
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            contactNumber: user.phone || "",
            email: user.email || "",
            vrPlay: "yes",
          },
        ],
      }));
    }
  }, []);

  // Update number of players
  const handlePlayersChange = (e) => {
    const newCount = parseInt(e.target.value, 10);
    setFormData((prev) => {
      const details = [...prev.playerDetails];
      while (details.length < newCount) {
        details.push({
          firstName: "",
          lastName: "",
          contactNumber: "",
          email: "",
          vrPlay: "yes",
        });
      }
      while (details.length > newCount) {
        details.pop();
      }
      return { ...prev, players: newCount, playerDetails: details };
    });
  };

  // Handle individual player input change
  const handlePlayerChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.playerDetails];
      updated[index][field] = value;
      return { ...prev, playerDetails: updated };
    });
  };

  const handlePaymentSuccess = async () => {
    try {
      const playersWithAmounts = calculatePlayerAmounts();

      const totalAmount = playersWithAmounts.reduce(
        (sum, p) => sum + p.amount,
        0,
      );

      const orderId = `ORDER_${Date.now()}`;
      const token = localStorage.getItem("authToken");

      const res = await axios.post(
        `${API_BASE_URL}/api/payhere/hash`,
        {
          order_id: orderId,
          amount: totalAmount.toFixed(2),
          currency: "LKR",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { hash, merchant_id } = res.data;

      const payment = {
        sandbox: true,
        merchant_id,
        return_url: window.location.href,
        cancel_url: window.location.href,
        notify_url: `${API_BASE_URL}/api/payhere/notify`,
        order_id: orderId,
        items: "Gaming Session Booking",
        amount: totalAmount.toFixed(2),
        currency: "LKR",
        hash,
        first_name: formData.playerDetails[0]?.firstName || "N/A",
        last_name: formData.playerDetails[0]?.lastName || "N/A",
        email: formData.playerDetails[0]?.email || "N/A",
        phone: formData.playerDetails[0]?.contactNumber || "N/A",
        address: "N/A",
        city: "N/A",
        country: "Sri Lanka",
      };

      window.payhere.onCompleted = async function () {
        const success = await onBookingSubmit(orderId);
        if (success) {
          setReceiptData({
            orderId: orderId,
            playerDetails: playersWithAmounts,
            station: selectedStation,
            date: selectedDateTime?.date,
            startTime: selectedDateTime?.time,
            duration: selectedDateTime?.duration,
          });
          setIsReceiptOpen(true);
        }
      };

      window.payhere.onDismissed = function () {
        showDialog("error", "Payment was cancelled.");
      };

      window.payhere.onError = function () {
        showDialog("error", "Payment error occurred. Please try again.");
      };

      window.payhere.startPayment(payment);
    } catch (err) {
      showDialog("error", "Failed to initiate payment. Please try again.");
      console.error(err);
    }
  };

  const calculatePlayerAmounts = () => {
    if (!selectedStation?.pricing?.length || !selectedDateTime) {
      return formData.playerDetails.map((p) => ({ ...p, amount: 0 }));
    }

    const pricingArray = selectedStation.pricing || [];
    const price30 = pricingArray.find((p) => Number(p.duration) === 30);
    const price60 = pricingArray.find((p) => Number(p.duration) === 60);

    const selectedDuration =
      Number(selectedDateTime?.durationMinutes || selectedDateTime?.duration) ||
      0;

    if (!selectedDuration || (!price30 && !price60)) {
      return formData.playerDetails.map((p) => ({ ...p, amount: 0 }));
    }

    // Derive missing tier from whichever exists
    const rate60Normal = price60
      ? parseFloat(price60.price || 0)
      : parseFloat(price30.price || 0) * 2;
    const rate60VR = price60
      ? parseFloat(price60.vrPrice || price60.price || 0)
      : parseFloat(price30.vrPrice || price30.price || 0) * 2;
    const rate30Normal = price30
      ? parseFloat(price30.price || 0)
      : parseFloat(price60.price || 0) / 2;
    const rate30VR = price30
      ? parseFloat(price30.vrPrice || price30.price || 0)
      : parseFloat(price60.vrPrice || price60.price || 0) / 2;

    return formData.playerDetails.map((player) => {
      const isVR = player.vrPlay === "yes";
      const hours = Math.floor(selectedDuration / 60);
      const remainingMinutes = selectedDuration % 60;

      let playerAmount = 0;
      if (hours > 0) {
        playerAmount += hours * (isVR ? rate60VR : rate60Normal);
      }
      if (remainingMinutes === 30) {
        playerAmount += isVR ? rate30VR : rate30Normal;
      }

      return { ...player, amount: playerAmount };
    });
  };

  const downloadSeparateReceipts = () => {
    if (!receiptData?.playerDetails?.length) return;

    receiptData.playerDetails.forEach((player, index) => {
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 20;

      // Dark background
      doc.setFillColor(11, 15, 25);
      doc.rect(0, 0, pageW, pageH, "F");

      // Header
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("GAMEVERSE", pageW / 2, 24, { align: "center" });

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(160, 160, 160);
      doc.text("Gaming Session Receipt", pageW / 2, 33, { align: "center" });
      doc.text(`Order ID: ${receiptData.orderId || "N/A"}`, pageW / 2, 41, { align: "center" });

      doc.setDrawColor(50, 55, 70);
      doc.line(margin, 47, pageW - margin, 47);

      // Build rows
      const rows = [
        ["Player Name", `${player.firstName} ${player.lastName}`],
        ["Phone Number", player.contactNumber || "N/A"],
        ["Station", receiptData.station?.name || "N/A"],
        ["Station Type", receiptData.station?.type || "-"],
      ];
      if (player.vrPlay) {
        rows.push(["VR Play", player.vrPlay === "yes" ? "Yes" : "No"]);
      }
      rows.push(
        ["Date", receiptData.date || "N/A"],
        ["Start Time", receiptData.startTime || "N/A"],
        ["Duration", receiptData.duration || "N/A"],
      );

      // Card
      const cardX = margin;
      const cardW = pageW - margin * 2;
      const rowH = 13;
      const cardH = 16 + rowH + rows.length * rowH + 8 + 34;
      const cardY = 55;

      doc.setFillColor(20, 26, 42);
      doc.rect(cardX, cardY, cardW, cardH, "F");
      doc.setDrawColor(50, 55, 70);
      doc.rect(cardX, cardY, cardW, cardH, "D");

      // Player heading
      let y = cardY + 14;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(`Player ${index + 1}`, cardX + 8, y);
      y += rowH + 2;

      // Detail rows
      rows.forEach(([label, value]) => {
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(160, 160, 160);
        doc.text(label, cardX + 8, y);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text(String(value), pageW - margin - 8, y, { align: "right" });
        y += rowH;
      });

      // Divider
      y += 4;
      doc.setDrawColor(50, 55, 70);
      doc.line(cardX + 8, y, pageW - margin - 8, y);
      y += 12;

      // Payment
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("Payment :", cardX + 8, y);
      doc.setTextColor(169, 5, 188);
      doc.text(
        `LKR ${player.amount?.toFixed(2) || "0.00"}`,
        pageW - margin - 8,
        y,
        { align: "right" },
      );

      // Footer
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 85, 100);
      doc.text(
        "Thank you for choosing Gameverse!",
        pageW / 2,
        pageH - 15,
        { align: "center" },
      );

      doc.save(`Receipt_${player.firstName}_${receiptData.orderId || index + 1}.pdf`);
    });
    navigate("/");
  };

  useEffect(() => {
    if (onPlayerInfoChange) onPlayerInfoChange(formData);
  }, [formData, onPlayerInfoChange]);
  const handleReceiptClose = () => {
    setIsReceiptOpen(false);
    navigate("/");
  };
  return (
    <Box sx={{ color: "white", mb: 8 }}>
      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          fontSize: { xs: "18px", sm: "24px", md: "32px" },
          mb: 6,
        }}
      >
        Player Info
      </Typography>

      <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
        {!["Pool", "Simulator"].includes(stationType) && (
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                mb: 1,
                fontSize: { xs: "11px", sm: "14px" },
                color: "gray.400",
              }}
            >
              Number of Players
            </Typography>
            <FormControl fullWidth>
              <Select
                name="players"
                value={formData.players}
                onChange={handlePlayersChange}
                displayEmpty
                sx={{
                  ...mainInputStyle,
                  "& .MuiSelect-select": {
                    color: "white",
                    padding: { xs: "10px 12px", sm: "14px" },
                    fontSize: { xs: "12px", sm: "14px" },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.1)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(51, 178, 247, 0.5)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#33B2F7",
                  },
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#0B0F19",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.1)",
                    },
                  },
                }}
              >
                {[1, 2, 3, 4].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} Player{num > 1 && "s"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Render Player Forms */}
        {formData.playerDetails.map((player, idx) => (
          <Box
            key={idx}
            sx={{
              mb: 6,
              p: 3,
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 2,
            }}
          >
            <Typography sx={{ mb: 2, fontWeight: "bold" }}>
              Player {idx + 1} {idx === 0 && "(Logged-in User)"}
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <TextField
                placeholder="First Name"
                value={player.firstName}
                disabled={idx === 0}
                onChange={(e) =>
                  handlePlayerChange(idx, "firstName", e.target.value)
                }
                sx={mainInputStyle}
              />
              <TextField
                placeholder="Last Name"
                value={player.lastName}
                disabled={idx === 0}
                onChange={(e) =>
                  handlePlayerChange(idx, "lastName", e.target.value)
                }
                sx={mainInputStyle}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 2,
                mt: 2,
              }}
            >
              <TextField
                placeholder="Email"
                value={player.email || ""}
                disabled={idx === 0} // logged user read-only
                onChange={(e) =>
                  handlePlayerChange(idx, "email", e.target.value)
                }
                sx={mainInputStyle}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: showVRPlay ? "1fr 1fr" : "1fr",
                gap: 2,
                mt: 2,
              }}
            >
              <TextField
                placeholder="Contact Number"
                value={player.contactNumber}
                disabled={idx === 0}
                onChange={(e) =>
                  handlePlayerChange(idx, "contactNumber", e.target.value)
                }
                sx={mainInputStyle}
              />

              {showVRPlay && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "8px", sm: "10px" },
                      color: "gray.400",
                      mb: { xs: 0, sm: 0.5 },
                    }}
                  >
                    VR Play
                  </Typography>
                  <RadioGroup
                    row
                    value={player.vrPlay}
                    onChange={(e) =>
                      handlePlayerChange(idx, "vrPlay", e.target.value)
                    }
                    sx={{
                      gap: { xs: 0.2, sm: 2 },
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={
                        <Radio
                          sx={{
                            color: "#33B2F7",
                            padding: { xs: "4px", sm: "9px" },
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: "18px", sm: "24px" },
                            },
                          }}
                        />
                      }
                      label="Yes"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: { xs: "12px", sm: "14px" },
                          color: "white",
                        },
                        margin: 0,
                      }}
                    />
                    <FormControlLabel
                      value="no"
                      control={
                        <Radio
                          sx={{
                            color: "#33B2F7",
                            padding: { xs: "4px", sm: "9px" },
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: "18px", sm: "24px" },
                            },
                          }}
                        />
                      }
                      label="No"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: { xs: "12px", sm: "14px" },
                          color: "white",
                        },
                        margin: 0,
                      }}
                    />
                  </RadioGroup>
                </Box>
              )}
            </Box>
          </Box>
        ))}
        <Box
          sx={{
            width: "100%",
            height: "1px",
            bgcolor: "rgba(255,255,255,0.1)",
            mb: 6,
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "25px",
                fontWeight: "bold",
                mb: 1,
                color: "white",
              }}
            >
              Amount
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "25px",
                fontWeight: "bold",
                background: "linear-gradient(to right, #A905BC, #33B2F7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              LKR {amount}
            </Typography>
            <Button
              onClick={handlePaymentSuccess}
              sx={{
                px: 8,
                py: 1.8,
                borderRadius: "30px",
                fontWeight: "bold",
                fontSize: "18px",
                textTransform: "none",
                color: "#fff",
                background: "linear-gradient(to right, #33B2F7, #A905BC)",
                boxShadow: "0 4px 15px rgba(51, 178, 247, 0.4)",
                "&:hover": {
                  background: "linear-gradient(to right, #A905BC, #33B2F7)",
                  boxShadow: "0 6px 20px rgba(169, 5, 188, 0.5)",
                },
              }}
            >
              Booking Session
            </Button>
          </Box>
        </Box>
      </Box>

      <BookingDetailsModal
        open={isReceiptOpen}
        onClose={handleReceiptClose}
        data={receiptData}
        downloadReceipts={downloadSeparateReceipts}
      />
      <AppDialog open={dialog.open} onClose={closeDialog} type={dialog.type} message={dialog.message} />
    </Box>
  );
};

// Helper for Styles
const mainInputStyle = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(255,255,255,0.05)",
    color: "white",
    fontSize: { xs: "12px", sm: "14px" },
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(51, 178, 247, 0.5)" },
    "&.Mui-focused fieldset": { borderColor: "#33B2F7" },
  },
  "& .MuiOutlinedInput-input": {
    color: "white",
    padding: { xs: "10px 12px", sm: "14px" },
    fontSize: { xs: "12px", sm: "14px" },
  },
  "&.Mui-disabled": {
    borderColor: "rgba(55, 65, 81, 0.62)",
    backgroundColor: "rgba(41, 37, 75, 0.58)",
    cursor: "text",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#E5E7EB",
    opacity: 1,
    cursor: "text",
  },
};

export default PlayerInfo;
